import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";
import { App, ColorPicker, Form, Input, Upload } from "antd";
import { useState } from "react";
import apiClient from "@/app/api/client";
import { fileEndpoints } from "@/app/api/endpoints";

interface EquipmentTypeFormProps {
	initialIconUrl?: string | null;
}

export function EquipmentTypeForm({ initialIconUrl }: EquipmentTypeFormProps) {
	const [uploading, setUploading] = useState(false);
	const [iconUrl, setIconUrl] = useState<string | null>(initialIconUrl ?? null);
	const { message } = App.useApp();
	const form = Form.useFormInstance();

	const beforeUpload = (file: File) => {
		const isPng = file.type === "image/png";
		if (!isPng) {
			message.error("Faqat PNG formatidagi fayl yuklash mumkin!");
			return false;
		}
		const isLt2M = file.size / 1024 / 1024 < 2;
		if (!isLt2M) {
			message.error("Fayl hajmi 2MB dan oshmasligi kerak!");
			return false;
		}
		return true;
	};

	const customRequest: UploadProps["customRequest"] = async ({ file, onSuccess, onError }) => {
		setUploading(true);
		const formData = new FormData();
		formData.append("file", file as File);
		formData.append("type", "PHOTO");
		try {
			const res = await apiClient.post(fileEndpoints.upload, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			if (res.data?.success) {
				const { id, url } = res.data.data as { id: string; url: string };
				setIconUrl(url);
				form.setFieldValue("icon", [id]);
				onSuccess?.(res.data);
			} else {
				throw new Error("Upload failed");
			}
		} catch (error) {
			onError?.(error as Error);
			message.error("Ikonka yuklashda xatolik yuz berdi");
		} finally {
			setUploading(false);
		}
	};

	return (
		<>
			<Form.Item
				name="name"
				label="Nomi"
				rules={[
					{ required: true, message: "Nomini kiriting" },
					{ max: 255, message: "Maksimum 255 ta belgi" },
				]}
			>
				<Input id="equipment-type-name" placeholder="Nomini kiriting" size="middle" />
			</Form.Item>

			<Form.Item name="icon" noStyle>
				<Input type="hidden" />
			</Form.Item>

			<Form.Item label="Ikonka (PNG)">
				<Upload
					accept=".png"
					listType="picture-card"
					showUploadList={false}
					beforeUpload={beforeUpload}
					customRequest={customRequest}
					disabled={uploading}
				>
					{iconUrl ? (
						<img
							src={iconUrl}
							alt="icon"
							style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }}
						/>
					) : (
						<div>
							{uploading ? <LoadingOutlined /> : <PlusOutlined />}
							<div style={{ marginTop: 8, fontSize: 12 }}>PNG yuklash</div>
						</div>
					)}
				</Upload>
			</Form.Item>

			<Form.Item name="color" label="Rang" getValueFromEvent={(color) => color.toHexString()}>
				<ColorPicker format="hex" showText />
			</Form.Item>

			<Form.Item name="description" label="Tavsif">
				<Input.TextArea
					placeholder="Texnika turi haqida qisqacha ma'lumot"
					rows={3}
					maxLength={500}
					showCount
				/>
			</Form.Item>
		</>
	);
}
